import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { auditKeys } from "@/lib/query";

export interface AuditFileRow {
  id: string;
  auditId: string;
  sourceItemId: string | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  status: string;
  uploadedById: string;
  uploadedByName: string | null;
  createdAt: string;
}

export function useAuditFiles(auditId: string | undefined) {
  return useQuery({
    queryKey: auditId ? auditKeys.files(auditId) : (["audits", "files", null] as const),
    enabled: !!auditId,
    queryFn: async (): Promise<AuditFileRow[]> => {
      const res = await fetch(`/api/audits/${auditId}/files`);
      if (!res.ok) throw new Error("Failed to fetch attachments");
      const json = await res.json();
      return json.data;
    },
  });
}

async function uploadOne(auditId: string, file: File, sourceItemId?: string) {
  const presignRes = await fetch(`/api/audits/${auditId}/files`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      sizeBytes: file.size,
      sourceItemId,
    }),
  });
  const presignJson = await presignRes.json();
  if (!presignRes.ok) throw new Error(presignJson.error ?? `Failed to prepare "${file.name}"`);

  const { id, uploadUrl } = presignJson.data;
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Failed to upload "${file.name}"`);

  const confirmRes = await fetch(`/api/audits/${auditId}/files/${id}/confirm`, {
    method: "POST",
  });
  if (!confirmRes.ok) throw new Error(`Failed to finalize "${file.name}"`);
}

export function useUploadAuditFiles(auditId: string, sourceItemId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (files: File[]) => {
      const results = await Promise.allSettled(
        files.map((file) => uploadOne(auditId, file, sourceItemId)),
      );
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
      return { total: files.length, failed: failures.map((f) => String(f.reason)) };
    },
    onSuccess: ({ total, failed }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.files(auditId) });
      if (failed.length === 0) {
        toast.success(total === 1 ? "File uploaded" : `${total} files uploaded`);
      } else if (failed.length === total) {
        toast.error(failed[0] ?? "Upload failed");
      } else {
        toast.error(`${failed.length} of ${total} files failed to upload`);
      }
    },
    onError: () => {
      toast.error("Upload failed");
    },
  });
}

export function useDeleteAuditFile(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: string) => {
      const res = await fetch(`/api/audits/${auditId}/files/${fileId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete attachment");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.files(auditId) });
      toast.success("Attachment deleted");
    },
    onError: () => {
      toast.error("Failed to delete attachment");
    },
  });
}

export async function openAuditFileDownload(auditId: string, fileId: string) {
  const res = await fetch(`/api/audits/${auditId}/files/${fileId}/download`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? "Failed to get download link");
  window.open(json.data.url, "_blank", "noopener,noreferrer");
}
